import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { TimeRangeFilter } from '../../../../shared/date/time-range-filter'
import { aiToolsAvailable, listClientApps } from './ai-tools-api'
import { CONSUMER_HELP, CONSUMER_RULE, DEFAULT_STATUS, RECEIVED_ANY, STATUS_FILTERS, isDay, isRange, personText } from './ai-tools-words'

// The lists' filters, in the house list page's toolbar (owner 2026-10-23:
// "we always put things in cards and buttons in headers"): one row of
// compact controls above the list's card (.sw-toolbar, as on the Request log
// and Errors), each named by its aria-label and hover, never a stacked label;
// its options say what they pick ("Any source", "Processed by anyone").
// markable-list.js draws the row; the list's actions are in its card's header.

// A select in the toolbar.
export const FilterSelect = ({ id, label, value, onChange, options, disabled, help }) =>
  <select id={id} className="form-select form-select-sm" aria-label={label} title={help || label} value={value} disabled={disabled} onChange={e => onChange(e.target.value)}>
    {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
  </select>

// The organization, sized to the row's selects.
export const OrganizationFilter = ({ id, value, onChange }) =>
  <div className="ai-org">
    <OrgPicker id={id} label="Organization" small value={value} onChange={onChange} allOption={{ value: '', label: 'Every organization' }} />
  </div>

// Processed by (a consumer) and a status. The server takes a status with or
// without a consumer: alone it looks across every consumer.
export const ConsumerStatusFilters = ({ consumers, consumer, status, onConsumer, onStatus }) => {
  const ids = { consumer: useId(), status: useId() }
  const options = [{ value: '', label: 'Processed by anyone' }, ...(consumers || []).map(name => ({ value: name, label: `Processed by ${name}` }))]
  if (consumer && !options.some(option => option.value === consumer)) options.push({ value: consumer, label: `Processed by ${consumer}` })
  return (
    <>
      <FilterSelect id={ids.consumer} label="Processed by" help={CONSUMER_HELP} value={consumer} options={options} onChange={onConsumer} />
      <FilterSelect id={ids.status} label="Status" value={status} options={STATUS_FILTERS} onChange={onStatus} />
    </>
  )
}

// The list's filters, kept in the address (owner 2026-10-29: like the Audit
// log and the Request log), so a reload, a copied link and Back from a
// session's page show the same list. spec (a module constant): { key: {
// initial, valid(value) } }; a key's parameter is its own name. A value equal
// to its initial one is left out of the address, and a parameter that isn't
// valid reads as the initial value. A filter whose initial value isn't empty
// (status: Needs processing) is turned off with an empty parameter
// (?status=), which reads as "any". -> { filters, update(patch) }: update
// replaces the current entry (a filter makes no history of its own) and keeps
// every other parameter (the tab, the grid's scroll, sort and search). It
// reads the address at the moment it writes, so two updates in one tick
// don't undo each other.
export const useListFilters = spec => {
  const location = useLocation()
  const history = useHistory()
  // the filters change only when one of their own parameters does (never on
  // the grid's scroll or sort keys), so the list isn't asked again for those
  const params = new URLSearchParams(location.search)
  const signature = JSON.stringify(Object.keys(spec).map(key => params.get(key)))
  const filters = useMemo(() => {
    const raw = JSON.parse(signature)
    return Object.fromEntries(Object.keys(spec).map((key, i) => {
      const value = raw[i]
      const { initial, valid } = spec[key]
      return [key, value === null || (valid && !valid(value)) ? initial : value]
    }))
  }, [signature, spec])
  const update = useCallback(patch => {
    const now = history.location
    const next = new URLSearchParams(now.search)
    Object.entries(patch).forEach(([key, value]) => {
      const text = value === null || value === undefined ? '' : String(value)
      if (text === spec[key].initial) next.delete(key)
      else next.set(key, text)
    })
    const query = next.toString()
    const search = query ? `?${query}` : ''
    if (search !== now.search) history.replace({ pathname: now.pathname, search, state: now.state })
  }, [history, spec])
  return { filters, update }
}

// The spec entries the lists share.
export const ORGANIZATION_SPEC = { initial: '', valid: value => /^\d*$/.test(value) }
// One person's rows (the Person column's filter; the server's login_id).
export const LOGIN_SPEC = { initial: '', valid: value => /^\d*$/.test(value) }

// The client app a session's header names (Alex's `clientApp`: "Claude Code",
// "Codex App"), in the address as ?client=.
export const CLIENT_SPEC = { initial: '', valid: value => [...value].length <= 128 }

// The client apps live sessions name, read once for the page (GET
// /admin/client-apps); none while it is read or when the read fails, and the
// filter still offers the one in the address.
export const useClientApps = () => {
  const [apps, setApps] = useState([])
  useEffect(() => {
    if (!aiToolsAvailable()) return undefined
    let stale = false
    listClientApps().then(answer => { if (!stale) setApps((answer && answer.clientApps) || []) }, () => {})
    return () => { stale = true }
  }, [])
  return apps
}

// "Any client app", then each one the sessions name.
export const ClientAppFilter = ({ id, value, onChange, apps }) => {
  const options = [{ value: '', label: 'Any client app' }, ...apps.map(app => ({ value: app, label: app }))]
  if (value && !apps.some(app => app.toLowerCase() === value.toLowerCase())) options.push({ value, label: value })
  return <FilterSelect id={id} label="Client app" help="The app the person ran the tools in, as the session's header names it" value={value} onChange={onChange} options={options} />
}
export const choiceSpec = (options, initial = '') => ({ initial, valid: value => options.some(option => option.value === value) })
export const CONSUMER_SPEC = { initial: '', valid: value => value === '' || CONSUMER_RULE.test(value) }
export const STATUS_SPEC = choiceSpec(STATUS_FILTERS, DEFAULT_STATUS)
export const RECEIVED_SPEC = {
  range: { initial: RECEIVED_ANY, valid: isRange },
  from: { initial: '', valid: isDay },
  to: { initial: '', valid: isDay }
}

// "Received": the house time range (shared/date/time-range-filter.js, as the
// Audit log draws it) in the toolbar -- Any time, the presets, or a custom
// range's From and To, each with its word beside it. value: the list's
// filters ({ range, from, to } in them); onChange(patch): the range's keys,
// a custom range's days dropped once another range is picked.
export const ReceivedFilter = ({ value, onChange }) =>
  <TimeRangeFilter label="Received" inline allTime value={{ range: value.range, from: value.from, to: value.to }}
    onChange={next => onChange(next.range === 'custom' ? { range: next.range, from: next.from, to: next.to } : { range: next.range, from: '', to: '' })} />

// The person filter in the toolbar, as a removable chip (the Request log's
// scoped filters): "Person: Ann Lee ✕". person: the row the filter came from
// (useMarkableList's personOf), else only the login's id is known.
export const PersonChip = ({ login, person, onClear }) => {
  if (!login) return null
  const name = person ? personText(person) : `login #${login}`
  return (
    <button type="button" className="sw-chip" aria-label={`Remove the person filter ${name}`} onClick={onClear}>
      Person: {name} <span aria-hidden="true">✕</span>
    </button>
  )
}
